import { absoluteUrl } from '@/shared/lib/env'
import { site } from '@/content/site'
import { team } from '@/content/team'
import type { Service } from '@/content/services'
import type { CaseStudy } from '@/content/case-studies'

/**
 * Structured data builders. All JSON-LD is constructed here rather than inline
 * in components, so the shapes stay consistent and testable.
 *
 * Fields that would require invented data are omitted rather than guessed —
 * structured data is a machine-readable claim about the business.
 */

export function organizationSchema() {
  const sameAs = site.socials.map((s) => s.url)
  const isRealEmail = !site.contact.email.startsWith('TODO')
  // Only claim a founder once the name is real — same rule as the email.
  const founder = team.find((m) => /founder/i.test(m.role) && !m.name.startsWith('TODO'))

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: site.name,
    url: absoluteUrl('/'),
    logo: absoluteUrl('/favicon.svg'),
    description: site.description,
    slogan: site.tagline,
    ...(sameAs.length > 0 && { sameAs }),
    ...(founder && {
      founder: {
        '@type': 'Person',
        name: founder.name,
        jobTitle: founder.role,
        ...(founder.image && { image: absoluteUrl(founder.image) }),
      },
    }),
    ...(isRealEmail && {
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'sales',
        email: site.contact.email,
        ...(site.contact.phone && { telephone: site.contact.phone }),
      },
    }),
    ...(site.contact.address && {
      address: {
        '@type': 'PostalAddress',
        // Only the parts that exist. Spreading conditionally rather than
        // assigning undefined keeps the emitted JSON-LD free of empty fields,
        // which validators flag.
        ...(site.contact.address.street && {
          streetAddress: site.contact.address.street,
        }),
        addressLocality: site.contact.address.city,
        ...(site.contact.address.region && {
          addressRegion: site.contact.address.region,
        }),
        ...(site.contact.address.postalCode && {
          postalCode: site.contact.address.postalCode,
        }),
        addressCountry: site.contact.address.country,
      },
    }),
  }
}

export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: site.name,
    url: absoluteUrl('/'),
    description: site.description,
  }
}

export function serviceSchema(service: Service) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.summary,
    url: absoluteUrl(`/services/${service.slug}`),
    serviceType: service.name,
    provider: {
      '@type': 'Organization',
      name: site.name,
      url: absoluteUrl('/'),
    },
  }
}

export function faqSchema(faqs: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  }
}

export function breadcrumbSchema(trail: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  }
}

export function caseStudySchema(study: CaseStudy) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: study.title,
    description: study.seo.description,
    url: absoluteUrl(`/work/${study.slug}`),
    author: { '@type': 'Organization', name: site.name },
    publisher: { '@type': 'Organization', name: site.name },
  }
}
